import type { CSSProperties, KeyboardEvent } from 'react';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { MapUserIcon } from '~/assets/icons';
import type { IPersonStatus } from '~/utils/interface/map';
import styles from '../Map.module.scss';
import type { TrackingDevice } from '../mapTrackingTypes';
import { getStatusColorWithOpacity, getStatusStyleVars } from '../statusPerson';

const cx = classNames.bind(styles);

const TERTIARY_TEXT = 'var(--Text-color_text_tertiary, #7C8796)';
const ICON_PRIMARY = 'var(--Icon-icon_pimary, #7C8796)';

interface UserMapMarkerProps {
  device: TrackingDevice;
  isHovered?: boolean;
  isSelected?: boolean;
  isDimmed?: boolean;
  onHoverChange?: (userId: number | null) => void;
  onSelect?: (userId: number) => void;
  personStatuses: IPersonStatus[];
}

function UserMapMarker({
  device,
  isHovered = false,
  isSelected = false,
  isDimmed = false,
  onHoverChange,
  onSelect,
  personStatuses,
}: UserMapMarkerProps) {
  const { t } = useTranslation('admin/map');
  const markerDimmed = isDimmed && !isHovered && !isSelected;
  const statusStyleVars = getStatusStyleVars(device.status, personStatuses) as CSSProperties;
  const rootStyle = markerDimmed ? undefined : statusStyleVars;
  const labelNameStyle = markerDimmed ? ({ color: TERTIARY_TEXT } as CSSProperties) : undefined;
  const dimmedIconBg = getStatusColorWithOpacity(device.status, personStatuses, 0.2);
  const dimmedIconBorder = getStatusColorWithOpacity(device.status, personStatuses, 0.38);
  const iconShellStyle = markerDimmed
    ? ({
        ...(dimmedIconBg ? { background: dimmedIconBg } : {}),
        borderColor: dimmedIconBorder ?? '#d7dee7',
      } as CSSProperties)
    : undefined;
  const iconGlyphStyle = markerDimmed ? ({ color: ICON_PRIMARY } as CSSProperties) : undefined;

  const handleSelect = () => onSelect?.(device.person_id);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect();
    }
  };

  const iconAria = t('marker.openDetails', { name: device.full_name });

  return (
    <div
      className={cx('UserMapMarker', 'marker', 'flex flex-col items-center select-none', {
        'marker-hovered': isHovered,
        'marker-selected': isSelected,
        'marker-dimmed': markerDimmed,
      })}
      style={rootStyle}
    >
      <div
        className={cx(
          'marker-label',
          'mb-1 inline-flex items-center justify-center rounded-[8px] border border-[#D7DEE7] bg-white px-2 py-1 whitespace-nowrap min-h-8 text-black'
        )}
      >
        <span className='text-[11px] font-semibold leading-none' style={labelNameStyle}>
          {device.full_name}
        </span>
      </div>
      <div
        className={cx(
          'marker-icon',
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-shadow duration-150'
        )}
        style={iconShellStyle}
        role='button'
        tabIndex={0}
        aria-label={iconAria}
        onMouseEnter={() => onHoverChange?.(device.person_id)}
        onMouseLeave={() => onHoverChange?.(null)}
        onTouchStart={() => onHoverChange?.(device.person_id)}
        onTouchEnd={() => onHoverChange?.(null)}
        onClick={handleSelect}
        onKeyDown={handleKeyDown}
      >
        <MapUserIcon className='h-5 w-5' style={iconGlyphStyle} />
      </div>
    </div>
  );
}

export default UserMapMarker;
